import { useState, useRef } from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import { memo } from "react";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 360,
  bgcolor: "#FFFFFF",
  boxShadow: 24,
  borderRadius: 2,
  p: 3,
};

const inputBaseStyle = {
  width: 56,
  height: 56,
  fontSize: 24,
  fontWeight: 600,
  textAlign: "center",
  border: "2px solid #79DCEB",
  borderRadius: 8,
  outline: "none",
  boxSizing: "border-box",
};

function PinVerificationModal({ open, expectedPin, onVerified }) {
  const [digits, setDigits] = useState(["", "", "", ""]);
  const [error, setError] = useState("");
  const inputRefs = useRef([null, null, null, null]);

  const normalizedExpected = expectedPin != null ? String(expectedPin).trim() : "";

  const handleChange = (index, value) => {
    const num = value.replace(/\D/g, "").slice(-1);
    const next = [...digits];
    next[index] = num;
    setDigits(next);
    setError("");
    if (num && index < 3) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
      const next = [...digits];
      next[index - 1] = "";
      setDigits(next);
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 4);
    const next = ["", "", "", ""];
    pasted.split("").forEach((c, i) => (next[i] = c));
    setDigits(next);
    setError("");
    const focusIdx = Math.min(pasted.length, 3);
    inputRefs.current[focusIdx]?.focus();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError("");
    const entered = digits.join("");
    if (entered.length !== 4) {
      setError("Please enter a 4-digit PIN.");
      return;
    }
    if (entered !== normalizedExpected) {
      setError("Incorrect PIN. Please try again.");
      return;
    }
    onVerified();
    setDigits(["", "", "", ""]);
    setError("");
  };

  return (
    <Modal
      open={open}
      onClose={(_, reason) => {
        if (reason === "backdropClick") return;
      }}
      aria-labelledby="pin-verification-title"
      aria-describedby="pin-verification-description"
      disableEscapeKeyDown
      BackdropProps={{ sx: { backgroundColor: "rgba(110, 110, 110, 0.85)" } }}
    >
      <Box sx={modalStyle}>
        <h3
          id="pin-verification-title"
          style={{
            margin: "0 0 32px 0",
            fontSize: 18,
            fontWeight: 700,
            color: "#000",
            textAlign: "left",
          }}
        >
          Enter the 4-digit SMS PIN to view tracking details
        </h3>
        <form onSubmit={handleSubmit}>
          <Box
            id="pin-verification-description"
            sx={{ display: "flex", gap: 1.5, justifyContent: "center", mb: 2, mt: 3 }}
          >
            {[0, 1, 2, 3].map((i) => (
              <input
                key={i}
                ref={(el) => (inputRefs.current[i] = el)}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digits[i]}
                onChange={(e) => handleChange(i, e.target.value)}
                onKeyDown={(e) => handleKeyDown(i, e)}
                onPaste={handlePaste}
                aria-label={`Digit ${i + 1} of PIN`}
                style={inputBaseStyle}
                className="pin-digit-input"
                autoComplete="one-time-code"
              />
            ))}
          </Box>
          {error && (
            <p style={{ margin: "0 0 12px 0", fontSize: 14, color: "#d32f2f", textAlign: "center" }}>
              {error}
            </p>
          )}
          <button
            type="submit"
            style={{
              width: "100%",
              padding: "12px 24px",
              fontSize: 16,
              fontWeight: 600,
              color: "#FFFFFF",
              backgroundColor: "#42C4DB",
              border: "none",
              borderRadius: 8,
              cursor: "pointer",
              textTransform: "uppercase",
            }}
          >
            Verify
          </button>
        </form>
      </Box>
    </Modal>
  );
}

export default memo(PinVerificationModal);
